import { MenuProps } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  TbAlarm,
  TbBriefcase,
  TbBuildingFactory,
  TbCalendarBolt,
  TbChartDots,
  TbChartDots3,
  TbChartHistogram,
  TbCube3dSphere,
  TbFolder,
  TbLayout2,
  TbLeaf,
  TbReceipt,
  TbReportAnalytics,
  TbSolarPanel2,
  TbSwitchHorizontal,
  TbTriangleSquareCircle,
  TbUrgent,
  TbUserCog,
} from "react-icons/tb";
import { Link } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { NAMESPACES } from "@/constants/i18next.constants";
import { ROUTES } from "@/constants/routes";
import { useAuthStore } from "@/features/Auth";
import { getUserConfig } from "@/features/Auth/Auth.utils";

import { useMainLayoutStore } from "../../MainLayout.store";
import { SIDEBAR_KEYS } from "./Sidebar.constants";
import { MenuItemTitleText } from "./Sidebar.styles";

type MenuItem = Required<MenuProps>["items"][number];

export const useGetSidebarItems = () => {
  const { t } = useTranslation(NAMESPACES.LAYOUT);
  const accountState = useAuthStore(state => state.accountState);
  const isSidebarCollapsed = useMainLayoutStore(useShallow(state => state.isSidebarCollapsed));

  const items = useMemo(() => {
    if (!accountState || !accountState.userType) {
      return [];
    }

    const userConfig = getUserConfig(accountState);

    const items: MenuItem[] = [
      {
        key: SIDEBAR_KEYS.MAIN,
        label: isSidebarCollapsed ? "" : <MenuItemTitleText>{t("sidebar.main.title")}</MenuItemTitleText>,
        type: "group",
        children: [
          {
            key: ROUTES.DASHBOARD,
            icon: <TbLayout2 size={18} />,
            label: (
              <Link to={ROUTES.DASHBOARD}>{t("sidebar.main.dashboard")}</Link>
            ),
          },
          {
            key: ROUTES.OSOS,
            icon: <TbBuildingFactory size={18} />,
            label: <Link to={ROUTES.OSOS}>{t("sidebar.main.osos")}</Link>,
          },
          {
            key: ROUTES.GENERATION,
            icon: <TbSolarPanel2 size={18} />,
            label: (
              <Link to={ROUTES.GENERATION}>{t("sidebar.main.generation")}</Link>
            ),
          },
          {
            key: ROUTES.ALARMS,
            icon: <TbAlarm size={18} />,
            label: (
              <Link to={ROUTES.ALARMS}>{t("sidebar.main.alarms")}</Link>
            ),
          },
          {
            key: ROUTES.REPORTS,
            icon: <TbReportAnalytics size={18} />,
            label: (
              <Link to={ROUTES.REPORTS}>{t("sidebar.main.reports")}</Link>
            ),
          },
        ],
      },
      {
        key: SIDEBAR_KEYS.MAIN_DIVIDER,
        type: "divider",
      },
      {
        key: SIDEBAR_KEYS.TOOLS,
        label: <MenuItemTitleText>{t("sidebar.tools.title")}</MenuItemTitleText>,
        icon: isSidebarCollapsed && <TbTriangleSquareCircle size={18} />,
        type: "submenu",
        children: [
          {
            key: ROUTES.BILLINGS,
            icon: <TbReceipt size={18} />,
            label: (
              <Link to={ROUTES.BILLINGS}>{t("sidebar.tools.billings")}</Link>
            ),
          },
          {
            key: ROUTES.BUDGET_PLANNING,
            icon: <TbCalendarBolt size={18} />,
            label: (
              <Link to={ROUTES.BUDGET_PLANNING}>
                {t("sidebar.tools.budgetPlanning")}
              </Link>
            ),
          },
          {
            key: ROUTES.SUSTAINABILITY,
            icon: <TbLeaf size={18} />,
            label: (
              <Link to={ROUTES.SUSTAINABILITY}>
                {t("sidebar.tools.sustainability")}
              </Link>
            ),
          },
        ],
      },
      {
        key: SIDEBAR_KEYS.TOOLS_DIVIDER,
        type: "divider",
      },
      {
        key: SIDEBAR_KEYS.SMART_DEVICES,
        label: <MenuItemTitleText>{t("sidebar.smartDevices.title")}</MenuItemTitleText>,
        icon: isSidebarCollapsed && <TbCube3dSphere size={18} />,
        type: "submenu",
        children: [
          {
            key: ROUTES.SENSORS,
            icon: <TbUrgent size={18} />,
            label: (
              <Link to={ROUTES.SENSORS}>{t("sidebar.smartDevices.sensors")}</Link>
            ),
          },
          {
            key: ROUTES.SENSOR_COMPARE,
            icon: <TbSwitchHorizontal size={18} />,
            label: (
              <Link to={ROUTES.SENSOR_COMPARE}>{t("sidebar.smartDevices.compare")}</Link>
            ),
          },
        ],
      },
      {
        key: SIDEBAR_KEYS.SMART_DEVICES_DIVIDER,
        type: "divider",
      },
      {
        key: SIDEBAR_KEYS.PREDICTION,
        label: <MenuItemTitleText>{t("sidebar.prediction.title")}</MenuItemTitleText>,
        icon: isSidebarCollapsed && <TbChartDots3 size={18} />,
        type: "submenu",
        children: [
          {
            key: ROUTES.BILLING_PREDICTION,
            icon: <TbChartHistogram size={18} />,
            label: (
              <Link to={ROUTES.BILLING_PREDICTION}>{t("sidebar.prediction.billingPrediction")}</Link>
            ),
          },
          {
            key: ROUTES.MCP_PREDICTION,
            icon: <TbChartDots size={18} />,
            label: (<Link to={ROUTES.MCP_PREDICTION}>{t("sidebar.prediction.mcpPrediction")}</Link>),
          },
        ],
      },
      {
        key: SIDEBAR_KEYS.PREDICTION_DIVIDER,
        type: "divider",
      },
      {
        key: SIDEBAR_KEYS.DOCUMENTS,
        label: (
          <MenuItemTitleText>{t("sidebar.documents.title")}</MenuItemTitleText>
        ),
        icon: isSidebarCollapsed && <TbBriefcase size={18} />,
        type: "submenu",
        children: [
          {
            key: ROUTES.DOCUMENTS,
            icon: <TbFolder size={18} />,
            label: (
              <Link to={ROUTES.DOCUMENTS}>
                {t("sidebar.documents.documents")}
              </Link>
            ),
          },
        ],
      },
      {
        key: SIDEBAR_KEYS.DOCUMENTS_DIVIDER,
        type: "divider",
      },
      {
        key: SIDEBAR_KEYS.ADMINISTRATION,
        icon: <TbUserCog size={18} />,
        label: (
          <Link to={ROUTES.ADMINISTRATION}>{t("sidebar.documents.administration")}</Link>
        ),
      },
    ];
    const filterItems = (item: ItemType) => {
      if (userConfig.hiddenRoutes?.includes(item?.key as string)
        || userConfig.hiddenSidebarItems?.includes(item?.key as string)) {
        return false;
      }
      return true;
    };

    return items.filter(filterItems).map((item) => {
      if (item?.type === "group") {
        return {
          ...item,
          children: item?.children?.filter(filterItems),
        };
      };
      return item;
    });
  }
  , [accountState,
    t,
    isSidebarCollapsed]);

  return items;
};
